import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'

import { ChartData } from 'chart.js'

import Page from 'commons/page'
import Block from 'components/block'
import { StackedBarGraph } from 'components/graph'
import { Horizontal } from 'components/layout'
import Panel from 'components/panel'
import { PropertyListTitle, SectionTitle } from 'components/typography'
import { useAuthProvider } from 'contexts/auth'
import { useAppDispatch, useAppSelector } from 'hooks'
import AccessDenied from 'pages/access-denied'

import {
  addDays,
  format,
  isSameDay,
  startOfDay,
  startOfWeek,
  subDays,
  subWeeks,
} from 'date-fns'
import {
  fetchContractById,
  selectContractById,
  selectContractByIdFailed,
  selectContractByIdLoading,
} from 'stores/contract'
import HelpTips from '../../../components/help-tip'
import LatestImportedDatetime from '../../../features/contracts/latest-imported-datetime'
import MonthTally from '../../../features/contracts/month-tally'
import { CountPerDay } from '../../../schema/contract'
import { UserRole } from '../../../schema/user'
import { useBreakpoints } from '../../../styles'
import theme from '../../../styles/theme'
import { BAR_GRAPH_OPTIONS, GRAPH_PLUGINS } from '../constants/graph'
import { createDiagonalPattern } from '../logic/graph'

// T6メインカラー(オレンジ)ベースのグラデーション配色
const CATEGORY_COLOR = {
  viewing: '#54350A', // 内見依頼: 濃茶
  document: '#A66A00', // 物件資料請求: 黄土
  purchase: theme.MAIN, // 購入申込: オレンジ (#F39800)
  inquiry: '#FFB84D', // 問い合わせ: 薄オレンジ(斜線パターンのベース色)
} as const

const GraphArea = styled(Horizontal)`
  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    flex-direction: column;
  }
`

const GraphColumn = styled.div`
  flex: 1;
`

// 日単位の抽出
const extractParDay = (items: { date: Date; quantity: number }[]) => {
  // 30日分のデータを表示対象とする
  const today = startOfDay(new Date())
  const ownDaysAgo = subDays(today, 1)
  // 集計対象の過去最終日
  const endDate = subDays(today, 30)

  const extractedData: number[] = []
  let currentDate = endDate

  while (currentDate <= ownDaysAgo) {
    const itemDate = startOfDay(currentDate)
    const foundItem = items.find((item) => {
      return isSameDay(item.date, itemDate)
    })

    if (foundItem) {
      extractedData.push(foundItem.quantity)
    } else {
      extractedData.push(0)
    }

    currentDate = addDays(currentDate, 1) // 次の日付に移動
  }
  return extractedData
}

// 週単位の抽出
const extractParWeek = (items: { date: Date; quantity: number }[]) => {
  // 30日分のデータを表示対象とする
  const today = startOfDay(new Date())
  const oneDaysAgo = subDays(today, 1)
  const endDate = subDays(today, 30) // 週単位の集計のため表示曜日による+曜日分を集計対象とする

  const extractedByWeek: { date: Date; quantity: number }[] = []

  let currentWeekStart = startOfWeek(oneDaysAgo, { weekStartsOn: 1 }) // 今週の開始日を取得
  while (currentWeekStart >= startOfWeek(endDate, { weekStartsOn: 1 })) {
    const currentWeekEnd = addDays(currentWeekStart, 6) // 今週の終了日を取得

    const weekItem = { date: currentWeekStart, quantity: 0 }

    for (const item of items) {
      const itemDate = startOfDay(item.date)

      if (itemDate >= currentWeekStart && itemDate <= currentWeekEnd) {
        weekItem.quantity += item.quantity
      }
    }

    extractedByWeek.push(weekItem)
    currentWeekStart = subDays(currentWeekStart, 7) // 前の週の開始日に移動
  }

  return extractedByWeek.map((value) => value.quantity).reverse()
}

function parDayLabel() {
  const today = startOfDay(new Date())
  const oneDaysAgo = subDays(today, 1)
  const endDate = subDays(today, 30)

  const dateList: string[] = []
  let currentDate = oneDaysAgo
  while (currentDate >= endDate) {
    const formattedDate = format(currentDate, 'MM/dd')
    dateList.push(formattedDate)
    currentDate = subDays(currentDate, 1) // 前の日付に移動
  }

  return dateList.reverse()
}

function parWeekLabel() {
  const today = new Date()
  const oneDaysAgo = subDays(today, 1)
  const endDate = subDays(today, 30)
  const weekStartDateList: string[] = []

  let currentWeekStart = startOfWeek(oneDaysAgo, { weekStartsOn: 1 }) // 今週の開始日（月曜日）を取得
  while (currentWeekStart >= startOfWeek(endDate, { weekStartsOn: 1 })) {
    const formattedWeekStartDate = format(currentWeekStart, 'MM/dd')
    weekStartDateList.push(formattedWeekStartDate)
    currentWeekStart = subWeeks(currentWeekStart, 1) // 前の週の開始日に移動
  }

  return weekStartDateList.reverse()
}

const getLabels = (isPc: boolean) => {
  if (isPc) {
    return parDayLabel()
  }

  return parWeekLabel()
}

const convertGraphData = (
  items: { date: Date; quantity: number }[],
  isPc: boolean
) => {
  if (!items) {
    return []
  }

  if (isPc) {
    return extractParDay(items)
  }

  return extractParWeek(items)
}

const Index = () => {
  const { claims } = useAuthProvider()
  const contract = useAppSelector(selectContractById)
  const contractLoading = useAppSelector(selectContractByIdLoading)
  const contractFailed = useAppSelector(selectContractByIdFailed)
  const dispatch = useAppDispatch()

  const { id } = useParams<{ id: string }>()
  const isOwner = useMemo(() => claims?.role === UserRole.OWNER, [claims?.role])

  const breakpoint = useBreakpoints()

  useEffect(() => {
    if (!claims?.role) {
      return
    }

    if (!id) {
      return
    }

    dispatch(fetchContractById({ role: claims?.role, id }))
  }, [dispatch, claims?.role, id])

  const HasApiError = useMemo(() => {
    return contractFailed
  }, [contractFailed])

  const isPc = useMemo(() => {
    return breakpoint === 'pc'
  }, [breakpoint])

  const graphLabels = useMemo(() => {
    return getLabels(isPc)
  }, [isPc])

  // 問い合わせ(エージェントのみ)は斜線パターンで区別する
  const inquiryPattern = useMemo(
    () => createDiagonalPattern(CATEGORY_COLOR.inquiry),
    []
  )

  const toGraphData = (countPerDay?: CountPerDay[]) =>
    convertGraphData(
      countPerDay?.map((item) => ({
        date: new Date(item.date._seconds * 1000),
        quantity: item.count,
      })) || [],
      isPc
    )

  // 集計対象カテゴリ（オーナーは「問い合わせ」を除く）
  const categories = useMemo(() => {
    const list = [
      {
        legendLabel: '内見依頼',
        tallyLabel: '内見依頼',
        color: CATEGORY_COLOR.viewing as string | CanvasPattern,
        data: toGraphData(contract?.other?.viewingRequestCountPerDay),
      },
      {
        legendLabel: '物件資料請求',
        tallyLabel: '物件資料請求',
        color: CATEGORY_COLOR.document as string | CanvasPattern,
        data: toGraphData(contract?.other?.documentRequestCountPerDay),
      },
      {
        legendLabel: '購入申込',
        tallyLabel: '購入申し込み',
        color: CATEGORY_COLOR.purchase as string | CanvasPattern,
        data: toGraphData(contract?.other?.purchaseApplicationCountPerDay),
      },
    ]
    if (!isOwner) {
      list.push({
        legendLabel: '問い合わせ',
        tallyLabel: '問い合わせ',
        color: inquiryPattern,
        data: toGraphData(contract?.other?.otherInquiryCountPerDay),
      })
    }
    return list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract, isPc, isOwner, inquiryPattern])

  const graphData: ChartData = useMemo(
    () => ({
      labels: graphLabels,
      datasets: categories.map((category) => ({
        label: category.legendLabel,
        data: category.data,
        backgroundColor: category.color,
        borderWidth: 0,
      })),
    }),
    [categories, graphLabels]
  )

  const sum = (values: number[]) => values.reduce((acc, num) => acc + num, 0)

  const tallyList = useMemo(
    () => [
      {
        label: '直近30日の反響件数',
        value: sum(categories.map((category) => sum(category.data))),
        unit: '回',
        detail: {
          list: categories.map((category) => ({
            label: category.tallyLabel,
            value: sum(category.data),
            unit: '回',
          })),
        },
      },
    ],
    [categories]
  )

  return (
    <>
      {HasApiError && <AccessDenied />}
      {!HasApiError && (
        <Page
          title={
            isOwner ? '業者からの問い合わせ' : 'フリエde物確からの問い合わせ'
          }
          loading={contractLoading}
          type="user"
          showBackButton={true}
        >
          <LatestImportedDatetime
            props={{ importedDate: contract?.other?.latestImportedDate }}
          />
          <Block>
            <PropertyListTitle>
              {isOwner
                ? '業者からの問い合わせデータ'
                : 'フリエde物確からの問い合わせデータ'}
            </PropertyListTitle>
          </Block>
          <Block>
            <SectionTitle>
              反響数
              <HelpTips
                text={
                  <>
                    直近30日間に寄せられた反響の件数です。
                    <br />
                    内見依頼・物件資料請求・購入申し込み
                    {!isOwner && '・問い合わせ'}を集計しています。
                  </>
                }
              />
            </SectionTitle>
            <Panel>
              <GraphArea justify="space-between">
                <GraphColumn>
                  <StackedBarGraph
                    data={graphData}
                    options={BAR_GRAPH_OPTIONS}
                    plugins={GRAPH_PLUGINS('件数')}
                  />
                </GraphColumn>
                <MonthTally list={tallyList} />
              </GraphArea>
            </Panel>
          </Block>
        </Page>
      )}
    </>
  )
}

export default Index
